// SearchSection.js
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, Heading, HStack, Icon, Link, Text } from '@chakra-ui/react';
import PostcodeSearch from '../components/PostcodeSearch';
import AddressSelectionModal from '../components/AddressSelectionModal';
import { FaSearch } from 'react-icons/fa';
import useSearchState from '../hooks/useSearch';
import useSearchHistory from '../hooks/useSearchHistory';
import useHousePrices from '../hooks/useHousePrices';
import usePlanningData from '../hooks/usePlanning';
import { makeSaveAddressData } from '../hooks/helpers/saveAddressDataHelper';
import SearchHistoryLimitationModal from '../components/SearchHistoryLimitationModal';
import { useAuth } from '../context/AuthContext';

function SearchSection() {
  const { currentUser } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalHistoryLimitOpen, setIsModalHistoryLimitOpen] = useState(false);
  const { selectPropertyFromSearch, updatePostCode, updateLoadingState, searchState } =
    useSearchState();

  const savedSearchId = searchState?.savedSearchId;
  const lastSalePrice = searchState?.lastSalePrice;
  const postcode = searchState?.postcode;
  const validatedAddress = searchState?.validatedAddress;
  const isLoadingSearchAddress = searchState?.isLoading;

  const { addSearch, searchHistory, error: saveSearchError } = useSearchHistory();
  const searchHistoryLimit =
    searchHistory && searchHistory.filter((search) => !!search.response_data).length >= 6;

  const { data: housePricesData, isLoading: isLoadingHousePricesData } = useHousePrices(
    validatedAddress?.postcode || null,
  );
  const { planningData, isLoading: isLoadingPlanningData } = usePlanningData(validatedAddress);

  const isLoadingData =
    isLoadingSearchAddress || isLoadingHousePricesData || isLoadingPlanningData;

  const onCloseSearchHistoryModal = () => setIsModalHistoryLimitOpen(false);

  const onAddressSelected = useCallback(
    (selectedProperty) => {
      selectPropertyFromSearch(selectedProperty);
    },
    [selectPropertyFromSearch],
  );

  useEffect(() => {
    if (validatedAddress && !isLoadingData && !savedSearchId) {
      const searchedAddress = makeSaveAddressData({
        data: validatedAddress,
        housePricesData,
        planningData,
        searchHistory,
        lastSalePrice,
      });
      if (!searchHistoryLimit) {
        addSearch(searchedAddress);
      }
    } else if (validatedAddress && !savedSearchId) {
      addSearch({
        ...validatedAddress,
        apiData: validatedAddress?.response_data,
      });
    }
  }, [validatedAddress, isLoadingData, searchHistoryLimit, savedSearchId]);

  useEffect(() => {
    if (
      saveSearchError?.status === 400 &&
      saveSearchError?.response?.data?.error?.toLowerCase().includes('reached your limit')
    ) {
      setIsModalHistoryLimitOpen(true);
    }
  }, [saveSearchError]);

  return (
    <>
      <Card variant='outline' bg='white' p={6} mb={8}>
        <HStack>
          <Icon as={FaSearch} color='blue.500' />
          <Heading size='md'>Property Search</Heading>
        </HStack>
        <Box mt={4}>
          <PostcodeSearch
            onAddressSelected={onAddressSelected}
            isLoadingSearchAddress={isLoadingSearchAddress}
            onToggleModal={setIsModalOpen}
            onUpdateLoadingState={updateLoadingState}
            onUpdatePostCode={updatePostCode}
            postcode={postcode}
            isModalOpen={isModalOpen}
          />
        </Box>
        <SearchHistoryLimitationModal
          open={isModalHistoryLimitOpen}
          onClose={onCloseSearchHistoryModal}
          message={false}
        />
        <AddressSelectionModal />

        {!currentUser && (
          <Box mt={4} bgColor='blue.50' color='blue.900' px={4} py={1.5} borderRadius='md'>
            <Text>
              Browsing as a guest.{' '}
              <Link href='/login' fontWeight={600} textDecoration='underline'>
                Log in
              </Link>{' '}
              or{' '}
              <Link href='/register' fontWeight={600} textDecoration='underline'>
                register
              </Link>{' '}
              to save your search history across devices.
            </Text>
          </Box>
        )}
      </Card>
      {!validatedAddress && (
        <Box py={10} textAlign='center'>
          <Text fontSize='md' color='gray.600'>
            Search for a UK property address above to view house prices and planning applications.
          </Text>
        </Box>
      )}
    </>
  );
}

export default React.memo(SearchSection);
